import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Plus, Search, Download, Briefcase } from 'lucide-react';
import { Button, Input, Card, Pagination, SortableHeader, AdvancedSearch, type SortDirection } from '../components/ui';
import { API_BASE_URL, getAuthToken } from '../lib/api';

interface Deal {
  id: string;
  dealId: string;
  dealName: string;
  accountName: string;
  dealType: string;
  dealStatus: string;
  dealProfit: string;
  dealOrderPlannedDate: string;
  updatedAt: string;
}

const DEAL_STATUS_OPTIONS = [
  { value: '見込み', label: '見込み' },
  { value: '提案中', label: '提案中' },
  { value: '交渉中', label: '交渉中' },
  { value: '受注', label: '受注' },
  { value: '失注', label: '失注' },
  { value: '保留', label: '保留' },
];

const DEAL_TYPE_OPTIONS = [
  { value: 'リユース', label: 'リユース' },
  { value: '新規', label: '新規' },
  { value: '追加', label: '追加' },
  { value: 'リプレイス', label: 'リプレイス' },
];

const SEARCH_FIELDS = [
  { key: 'dealId', label: '案件ID', type: 'text' as const, placeholder: 'DEAL-001' },
  { key: 'dealName', label: '案件名', type: 'text' as const, placeholder: '案件名...' },
  { key: 'accountName', label: '企業名', type: 'text' as const, placeholder: '企業名...' },
  { key: 'dealType', label: '種別', type: 'select' as const, options: DEAL_TYPE_OPTIONS },
  { key: 'dealStatus', label: 'ステータス', type: 'select' as const, options: DEAL_STATUS_OPTIONS },
  { key: 'dealOrderPlannedDate', label: '受注予定日', type: 'daterange' as const },
  { key: 'updatedAt', label: '更新日', type: 'daterange' as const },
];

export function Deals() {
  const navigate = useNavigate();
  const [deals, setDeals] = useState<Deal[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  
  // 検索条件
  const [search, setSearch] = useState('');
  const [filters, setFilters] = useState<Record<string, string>>({});
  
  // ページネーション
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(30);
  const [totalPages, setTotalPages] = useState(1);
  const [total, setTotal] = useState(0);
  
  // ソート
  const [sortKey, setSortKey] = useState<string | null>('updatedAt');
  const [sortDirection, setSortDirection] = useState<SortDirection>('desc');

  useEffect(() => {
    fetchDeals();
  }, [page, limit, sortKey, sortDirection]);

  const fetchDeals = async () => {
    setIsLoading(true);
    try {
      const params = new URLSearchParams({
        page: page.toString(),
        limit: limit.toString(),
        ...(search && { search }),
        ...(sortKey && sortDirection && { sortBy: sortKey, sortOrder: sortDirection }),
        ...Object.fromEntries(
          Object.entries(filters).filter(([, v]) => v !== '')
        ),
      });
      
      const headers: Record<string, string> = {};
      const token = getAuthToken();
      if (token) {
        headers['Authorization'] = `Bearer ${token}`;
      }
      
      const response = await fetch(`${API_BASE_URL}/api/deals?${params}`, {
        credentials: 'include',
        headers,
      });
      const data = await response.json();
      
      setDeals(data.data || []);
      setTotalPages(data.pagination?.totalPages || 1);
      setTotal(data.pagination?.total || 0);
    } catch (err) {
      console.error('Fetch deals error:', err);
    } finally {
      setIsLoading(false);
    }
  };

  const handleSearch = () => {
    setPage(1);
    fetchDeals();
  };

  const handleReset = () => {
    setSearch('');
    setFilters({});
    setPage(1);
    fetchDeals();
  };

  const handleSort = (key: string, direction: SortDirection) => {
    setSortKey(direction ? key : null);
    setSortDirection(direction);
    setPage(1);
  };

  const handleLimitChange = (newLimit: number) => {
    setLimit(newLimit);
    setPage(1);
  };

  const getStatusBadge = (status: string) => {
    const badges: Record<string, string> = {
      '見込み': 'bg-blue-100 text-blue-700',
      '提案中': 'bg-purple-100 text-purple-700',
      '交渉中': 'bg-yellow-100 text-yellow-700',
      '受注': 'bg-green-100 text-green-700',
      '失注': 'bg-red-100 text-red-700',
      '保留': 'bg-gray-100 text-gray-700',
    };
    return badges[status] || 'bg-gray-100 text-gray-700';
  };

  const formatCurrency = (amount: string | number) => {
    const num = typeof amount === 'string' ? parseFloat(amount) : amount;
    if (isNaN(num)) return '-';
    return new Intl.NumberFormat('ja-JP', { 
      style: 'currency', 
      currency: 'JPY',
      maximumFractionDigits: 0,
    }).format(num);
  };

  return (
    <div className="space-y-6">
      {/* ヘッダー */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold" style={{ color: 'var(--text-primary)' }}>
            案件管理
          </h1>
          <p style={{ color: 'var(--text-secondary)' }}>
            {total.toLocaleString()} 件の案件
          </p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" leftIcon={<Download size={16} />}>
            CSV出力
          </Button>
          <Button leftIcon={<Plus size={16} />} onClick={() => navigate('/deals/new')}>
            新規作成
          </Button>
        </div>
      </div>

      {/* 検索・フィルター */}
      <Card padding="sm">
        <AdvancedSearch
          fields={SEARCH_FIELDS}
          values={filters}
          onChange={(key, value) => setFilters(prev => ({ ...prev, [key]: value }))}
          onSearch={handleSearch}
          onReset={handleReset}
        >
          <div className="flex-1 min-w-[200px]">
            <Input
              placeholder="案件名・案件IDで検索..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              leftIcon={<Search size={18} />}
            />
          </div>
          <Button type="button" onClick={handleSearch}>
            検索
          </Button>
        </AdvancedSearch>
      </Card>

      {/* テーブル */}
      <Card padding="none">
        {isLoading ? (
          <div className="flex items-center justify-center h-64">
            <p style={{ color: 'var(--text-secondary)' }}>読み込み中...</p>
          </div>
        ) : deals.length === 0 ? (
          <div className="flex flex-col items-center justify-center h-64">
            <Briefcase size={48} className="text-gray-300 mb-4" />
            <p style={{ color: 'var(--text-secondary)' }}>案件がありません</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr>
                  <SortableHeader
                    label="案件名"
                    sortKey="dealName"
                    currentSortKey={sortKey}
                    currentDirection={sortDirection}
                    onSort={handleSort}
                  />
                  <SortableHeader
                    label="企業"
                    sortKey="accountName"
                    currentSortKey={sortKey}
                    currentDirection={sortDirection}
                    onSort={handleSort}
                  />
                  <SortableHeader
                    label="種別"
                    sortKey="dealType"
                    currentSortKey={sortKey}
                    currentDirection={sortDirection}
                    onSort={handleSort}
                  />
                  <SortableHeader
                    label="ステータス"
                    sortKey="dealStatus"
                    currentSortKey={sortKey}
                    currentDirection={sortDirection}
                    onSort={handleSort}
                  />
                  <SortableHeader
                    label="利益"
                    sortKey="dealProfit"
                    currentSortKey={sortKey}
                    currentDirection={sortDirection}
                    onSort={handleSort}
                    align="right"
                  />
                  <SortableHeader
                    label="受注予定日"
                    sortKey="dealOrderPlannedDate"
                    currentSortKey={sortKey}
                    currentDirection={sortDirection}
                    onSort={handleSort}
                  />
                </tr>
              </thead>
              <tbody className="divide-y" style={{ borderColor: 'var(--border-color)' }}>
                {deals.map((deal) => (
                  <tr
                    key={deal.id}
                    className="hover:bg-gray-50 cursor-pointer transition-colors"
                    onClick={() => navigate(`/deals/${deal.id}`)}
                  >
                    <td className="px-4 py-3">
                      <div>
                        <p className="font-medium" style={{ color: 'var(--text-primary)' }}>
                          {deal.dealName}
                        </p>
                        <p className="text-xs" style={{ color: 'var(--text-secondary)' }}>
                          {deal.dealId}
                        </p>
                      </div>
                    </td>
                    <td className="px-4 py-3" style={{ color: 'var(--text-primary)' }}>
                      {deal.accountName || '-'}
                    </td>
                    <td className="px-4 py-3" style={{ color: 'var(--text-secondary)' }}>
                      {deal.dealType || '-'}
                    </td>
                    <td className="px-4 py-3">
                      <span className={`px-2 py-1 text-xs rounded-full ${getStatusBadge(deal.dealStatus)}`}>
                        {deal.dealStatus || '-'}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-right font-medium" style={{ color: 'var(--text-primary)' }}>
                      {formatCurrency(deal.dealProfit)}
                    </td>
                    <td className="px-4 py-3 text-sm" style={{ color: 'var(--text-secondary)' }}>
                      {deal.dealOrderPlannedDate 
                        ? new Date(deal.dealOrderPlannedDate).toLocaleDateString('ja-JP')
                        : '-'
                      }
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {/* ページネーション */}
        {!isLoading && total > 0 && (
          <Pagination
            page={page}
            totalPages={totalPages}
            total={total}
            limit={limit}
            onPageChange={setPage}
            onLimitChange={handleLimitChange}
          />
        )}
      </Card>
    </div>
  );
}
